import type { SeoDescriptor } from './seoConfig';

// Low-level <head> upserts. Dependency-free (no react-helmet → no lock churn).
// Client-only (touch `document`); safe to import from SSR modules because the
// `document` access is inside the function bodies, not at module top-level.

export const upsertMeta = (
    attr: 'name' | 'property',
    key: string,
    content: string,
): void => {
    let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
};

export const upsertCanonical = (href: string): void => {
    let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
};

export const syncJsonLd = (blocks: readonly object[]): void => {
    document.head
        .querySelectorAll('script[type="application/ld+json"]')
        .forEach((n) => n.remove());
    for (const block of blocks) {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.textContent = JSON.stringify(block);
        document.head.appendChild(s);
    }
};

/** Set a `<meta property>` when content is given, else remove it — so stale
 *  `article:*` tags don't linger after navigating off an article. */
const setOrRemoveMeta = (property: string, content: string | undefined): void => {
    if (content) upsertMeta('property', property, content);
    else document.head.querySelector(`meta[property="${property}"]`)?.remove();
};

/**
 * Apply a resolved SEO descriptor to <head>: title, meta description, canonical,
 * Open Graph / Twitter tags and JSON-LD. Used on client-side navigation; the
 * build-time prerender bakes the same tags into the static HTML for crawlers.
 */
export const applySeo = (d: SeoDescriptor, opts?: { skipTitle?: boolean }): void => {
    // skipTitle: leave the visible <title> (browser tab) to another owner — e.g.
    // the coin terminal's live "{symbol} | ${mc} | Fasol" ticker, a user-facing
    // feature we must not change for SEO. We still set og:title/twitter:title
    // from d.title, so the keyword-rich SEO title stays the canonical crawler
    // signal (Google commonly uses og:title for the SERP title).
    if (!opts?.skipTitle) document.title = d.title;
    upsertMeta('name', 'description', d.description);
    upsertCanonical(d.canonical);
    // robots: noindex thin/below-gate dynamic pages; otherwise clear any prior
    // noindex (default = indexable → no tag) so it doesn't linger on nav.
    if (d.noindex) upsertMeta('name', 'robots', 'noindex,follow');
    else document.head.querySelector('meta[name="robots"]')?.remove();
    upsertMeta('property', 'og:type', d.ogType);
    upsertMeta('property', 'og:title', d.title);
    upsertMeta('property', 'og:description', d.description);
    upsertMeta('property', 'og:url', d.canonical);
    upsertMeta('property', 'og:image', d.ogImage);
    setOrRemoveMeta('article:published_time', d.publishedTime);
    setOrRemoveMeta('article:modified_time', d.modifiedTime);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', d.title);
    upsertMeta('name', 'twitter:description', d.description);
    upsertMeta('name', 'twitter:image', d.ogImage);
    syncJsonLd(d.jsonLd);
};
